import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonGroupItem,
  Legend,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
  Radio,
  RadioGroup,
} from "@boozallen-uip/uswds-react-core";
import { IndeterminateSpinner } from "@boozallen-uip/uswds-react-loading";
import downloadAcrHtml from "../../../components/acr-editor/utils/acrExport/downloadAcrHtml";
import downloadAcrJson from "../../../components/acr-editor/utils/acrExport/downloadAcrJson";
import downloadAcrYaml from "../../../components/acr-editor/utils/acrExport/downloadAcrYaml";
import downloadAcrZip from "../../../components/acr-editor/utils/acrExport/downloadAcrZip";

const FILE_TYPE_OPTIONS = [
  { value: "html", label: "HTML (.html)" },
  { value: "json", label: "JSON (.json)" },
  { value: "yaml", label: "YAML (.yaml)" },
];

const DOWNLOADERS = {
  html: downloadAcrHtml,
  json: downloadAcrJson,
  yaml: downloadAcrYaml,
};

/**
 * Bulk export wizard for the Product Listing page. Given the products whose in-site (ACR Editor)
 * ACRs the user checked, it asks for a file type (HTML / JSON / YAML) then downloads them: a single
 * ACR downloads as one file, multiple ACRs are bundled into one compressed .zip. Building the export
 * model per product reuses the same loader the product detail page uses, so exports stay faithful to
 * the editor output.
 */
export default function AcrExportWizard({ isOpen, onClose, products, buildExportModel }) {
  const [fileType, setFileType] = useState("html");
  const [status, setStatus] = useState("choose");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [failedProducts, setFailedProducts] = useState([]);
  const [result, setResult] = useState({ succeeded: 0, zipped: false });

  const productCount = products.length;

  const resetState = useCallback(() => {
    setFileType("html");
    setStatus("choose");
    setProgress({ done: 0, total: 0 });
    setFailedProducts([]);
    setResult({ succeeded: 0, zipped: false });
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const handleClose = useCallback(() => {
    if (status === "exporting") {
      return;
    }
    onClose();
  }, [onClose, status]);

  const handleDownload = useCallback(async () => {
    if (!productCount) {
      return;
    }
    const downloader = DOWNLOADERS[fileType];
    if (!downloader) {
      return;
    }

    setStatus("exporting");
    setProgress({ done: 0, total: productCount });
    const failures = [];
    const built = [];

    for (let index = 0; index < products.length; index += 1) {
      const product = products[index];
      // Sequential on purpose: build one ACR model at a time so we can report per-product failures
      // and avoid firing a large burst of concurrent catalog requests. Files are delivered in one
      // shot after all models are built (a single file, or one zip for multiple).
      // eslint-disable-next-line no-await-in-loop
      const model = await buildExportModel(product);
      if (model?.ok && model.evaluation && model.catalog) {
        built.push({ product, evaluation: model.evaluation, catalog: model.catalog });
      } else {
        failures.push(product);
      }
      setProgress({ done: index + 1, total: productCount });
    }

    let zipped = false;
    try {
      if (built.length === 1) {
        downloader(built[0].evaluation, built[0].catalog);
      } else if (built.length > 1) {
        downloadAcrZip(built, fileType);
        zipped = true;
      }
    } catch {
      // Packaging/download itself failed: none of the built ACRs reached the user.
      built.forEach((item) => failures.push(item.product));
      built.length = 0;
    }

    setResult({ succeeded: built.length, zipped });
    setFailedProducts(failures);
    setStatus("done");
  }, [buildExportModel, fileType, productCount, products]);

  const selectedFileTypeLabel = useMemo(
    () => FILE_TYPE_OPTIONS.find((option) => option.value === fileType)?.label ?? fileType,
    [fileType]
  );

  const succeededCount = result.succeeded;

  return (
    <Modal
      id="acr-export-wizard"
      labelledBy="acr-export-wizard-heading"
      describedBy="acr-export-wizard-body"
      isOpen={isOpen}
      onStateChanged={(state) => {
        // USWDS reports its own open/close; keep our state in sync when the user dismisses it.
        if (state?.isOpen === false) {
          handleClose();
        }
      }}
      forceAction={status === "exporting"}
    >
      <ModalHeading id="acr-export-wizard-heading">Export ACRs</ModalHeading>

      <ModalBody id="acr-export-wizard-body">
        {status === "choose" ? (
          <>
            <p className="mt-0">
              {productCount === 1
                ? "1 ACR is selected for export."
                : `${productCount} ACRs are selected for export and will download together as a single .zip.`}{" "}
              Choose the file format you want to download.
            </p>
            <RadioGroup
              id="acr-export-file-type"
              name="acr-export-file-type"
              value={fileType}
              onChange={(e) => setFileType(e.detail.value)}
            >
              <Legend>File format</Legend>
              {FILE_TYPE_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
          </>
        ) : null}

        {status === "exporting" ? (
          <div role="status" aria-live="polite">
            <p className="mt-0">
              Preparing {selectedFileTypeLabel} download… ({progress.done} of {progress.total})
            </p>
            <IndeterminateSpinner type="circle" color="blue" />
          </div>
        ) : null}

        {status === "done" ? (
          <div role="status" aria-live="polite">
            {succeededCount > 0 ? (
              <p className="mt-0">
                {succeededCount === 1
                  ? `1 ACR was downloaded as ${selectedFileTypeLabel}.`
                  : `${succeededCount} ACRs were downloaded as ${selectedFileTypeLabel}${result.zipped ? " in a single .zip (acr-export.zip)" : ""}.`}
              </p>
            ) : null}
            {failedProducts.length > 0 ? (
              <Alert type="warning" heading="Some ACRs could not be exported">
                <ul className="mt-1 mb-0">
                  {failedProducts.map((product) => (
                    <li key={product.id}>{product.name || `Product ${product.id}`}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <ButtonGroup>
          {status === "choose" ? (
            <>
              <ButtonGroupItem>
                <Button onClick={handleDownload} disabled={productCount === 0}>
                  Download
                </Button>
              </ButtonGroupItem>
              <ButtonGroupItem>
                <Button unstyled className="padding-105 text-center" onClick={handleClose}>
                  Cancel
                </Button>
              </ButtonGroupItem>
            </>
          ) : null}

          {status === "done" ? (
            <ButtonGroupItem>
              <Button onClick={handleClose}>Close</Button>
            </ButtonGroupItem>
          ) : null}
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  );
}

AcrExportWizard.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
  buildExportModel: PropTypes.func.isRequired,
};
